/**
 * m3 echo Frontend - UI Store
 *
 * Centralized UI state management using Zustand with persistence.
 * Handles dialog state, filters, and UI preferences.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DashboardFilters {
  searchTerm: string;
}

interface UIState {
  // Dialog state
  createProjectDialogOpen: boolean;
  createJobDialogOpen: boolean;
  jobDetailDialogOpen: boolean;
  selectedJobId: number | null;

  // Delete confirmation
  deleteProjectDialogOpen: boolean;
  projectToDelete: number | null;

  // Filter state (persisted)
  dashboardFilters: DashboardFilters;

  // Settings state (persisted)
  settingsActiveTab: number;

  // Admin state (persisted)
  adminActiveTab: number;

  // Tools state (persisted)
  toolsActiveTab: number;

  // Wiki state (persisted)
  wikiActiveSection: string;

  // Billing state (persisted)
  billingActiveTab: number;

  // Collapsible sections
  filtersExpanded: boolean;

  // Actions - Dialogs
  openCreateProjectDialog: () => void;
  closeCreateProjectDialog: () => void;

  openCreateJobDialog: () => void;
  closeCreateJobDialog: () => void;

  openJobDetailDialog: (jobId: number) => void;
  closeJobDetailDialog: () => void;

  openDeleteProjectDialog: (projectId: number) => void;
  closeDeleteProjectDialog: () => void;
  confirmDeleteProject: () => number | null;

  // Actions - Filters
  setSearchTermFilter: (searchTerm: string) => void;
  clearDashboardFilters: () => void;

  // Actions - Settings
  setSettingsTab: (tab: number) => void;

  // Actions - Admin
  setAdminTab: (tab: number) => void;

  // Actions - Tools
  setToolsTab: (tab: number) => void;

  // Actions - Wiki
  setWikiSection: (section: string) => void;

  // Actions - Billing
  setBillingTab: (tab: number) => void;

  // Actions - UI
  toggleFiltersExpanded: () => void;
  setFiltersExpanded: (expanded: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      createProjectDialogOpen: false,
      createJobDialogOpen: false,
      jobDetailDialogOpen: false,
      selectedJobId: null,
      deleteProjectDialogOpen: false,
      projectToDelete: null,
      dashboardFilters: {
        searchTerm: '',
      },
      settingsActiveTab: 0,
      adminActiveTab: 0,
      toolsActiveTab: 0,
      wikiActiveSection: 'overview',
      billingActiveTab: 0,
      filtersExpanded: false,

      // Dialog actions
      openCreateProjectDialog: () => set({ createProjectDialogOpen: true }),
      closeCreateProjectDialog: () => set({ createProjectDialogOpen: false }),

      openCreateJobDialog: () => set({ createJobDialogOpen: true }),
      closeCreateJobDialog: () => set({ createJobDialogOpen: false }),

      openJobDetailDialog: (jobId: number) => set({
        jobDetailDialogOpen: true,
        selectedJobId: jobId
      }),
      closeJobDetailDialog: () => set({
        jobDetailDialogOpen: false,
        selectedJobId: null
      }),

      openDeleteProjectDialog: (projectId: number) => set({
        deleteProjectDialogOpen: true,
        projectToDelete: projectId
      }),
      closeDeleteProjectDialog: () => set({
        deleteProjectDialogOpen: false,
        projectToDelete: null
      }),
      confirmDeleteProject: () => {
        const projectId = get().projectToDelete;
        set({
          deleteProjectDialogOpen: false,
          projectToDelete: null
        });
        return projectId;
      },

      // Filter actions
      setSearchTermFilter: (searchTerm: string) => set(state => ({
        dashboardFilters: {
          ...state.dashboardFilters,
          searchTerm
        }
      })),
      clearDashboardFilters: () => set({
        dashboardFilters: {
          searchTerm: ''
        }
      }),

      // Settings actions
      setSettingsTab: (tab: number) => set({ settingsActiveTab: tab }),

      // Admin actions
      setAdminTab: (tab: number) => set({ adminActiveTab: tab }),

      // Tools actions
      setToolsTab: (tab: number) => set({ toolsActiveTab: tab }),

      // Wiki actions
      setWikiSection: (section: string) => set({ wikiActiveSection: section }),

      // Billing actions
      setBillingTab: (tab: number) => set({ billingActiveTab: tab }),

      // UI actions
      toggleFiltersExpanded: () => set(state => ({
        filtersExpanded: !state.filtersExpanded
      })),
      setFiltersExpanded: (expanded: boolean) => set({ filtersExpanded: expanded }),
    }),
    {
      name: 'm3echo-ui-storage',
      // Only persist these fields
      partialize: (state) => ({
        dashboardFilters: state.dashboardFilters,
        settingsActiveTab: state.settingsActiveTab,
        adminActiveTab: state.adminActiveTab,
        toolsActiveTab: state.toolsActiveTab,
        wikiActiveSection: state.wikiActiveSection,
        billingActiveTab: state.billingActiveTab,
        filtersExpanded: state.filtersExpanded,
      }),
    }
  )
);
